import { motion } from "framer-motion";
import { AnimatedSection, StaggerContainer, StaggerItem, Card3D, FloatingElement } from "../../../components/animations/AnimatedElements";

const highlights = [
  {
    icon: "ri-layout-grid-line",
    title: "ADX 통합 플랫폼",
    desc: "AI와 Digital Twin을 하나로 통합한 ADX 플랫폼 기반으로 고객 맞춤형 솔루션을 설계·공급합니다.",
  },
  {
    icon: "ri-lightbulb-line",
    title: "컨설팅 우선 접근",
    desc: "고객 현장을 직접 분석하고 최적의 AI·디지털트윈 도입 전략을 컨설팅으로 제시합니다.",
  },
  {
    icon: "ri-code-box-line",
    title: "맞춤 솔루션 개발",
    desc: "컨설팅 결과를 바탕으로 공공기관·민간기업 특성에 맞는 서비스와 솔루션을 직접 구축합니다.",
  },
  {
    icon: "ri-global-line",
    title: "글로벌 확장 목표",
    desc: "국내 우수조달 및 TOP 5 기업을 넘어 글로벌 AI·디지털트윈 시장으로 진출합니다.",
  },
];

const platformSteps = ["고객 현장 분석", "ADX 컨설팅", "AI·DT 솔루션 구축", "운영·고도화"];

export default function AboutSection() {
  return (
    <section id="about" className="py-28 bg-brand-dark relative overflow-hidden">
      {/* Background Glows */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        <AnimatedSection>
          <div className="section-header">
            <span className="section-accent" />
            <span className="section-label">About CITY-AI</span>
          </div>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="section-title leading-tight mb-6">
                AI & Digital Twin,<br />
                <span className="text-cyan-400">ADX 플랫폼</span>으로<br />
                비즈니스를 바꿉니다
              </h2>
              <p className="text-gray-400 leading-relaxed mb-4 text-base">
                <strong className="text-white">CITY-AI(씨티에이아이) 주식회사</strong>는 단순한 용역 회사가 아닙니다.
                AI와 Digital Twin을 통합한 <strong className="text-cyan-400">ADX(AI & Digital Twin eXperience) 플랫폼</strong>을
                기반으로 고객의 현장을 분석하고 컨설팅하여, 최적의 맞춤형 서비스와 솔루션을 구축해드리는 <strong className="text-white">통합 AI 혁신 파트너</strong>입니다.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8 text-base">
                공공기관부터 민간기업까지, 고객이 직면한 문제를 ADX 플랫폼으로 진단하고
                AI Agent·디지털트윈·영상분석·데이터 인텔리전스를 결합한
                <strong className="text-white"> end-to-end 솔루션</strong>을 제공합니다.
              </p>

              {/* ADX Platform Structure */}
              <motion.div
                className="bg-brand-darkCard border border-cyan-900/30 rounded-lg p-5 mb-6"
                whileHover={{ borderColor: "rgba(0, 229, 255, 0.3)" }}
              >
                <p className="text-cyan-400 text-xs font-bold tracking-widest mb-3">ADX PLATFORM 구조</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {platformSteps.map((step, idx) => (
                    <motion.span
                      key={step}
                      className="flex items-center gap-2"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <span className="text-xs px-2 py-1 bg-cyan-500/10 border border-cyan-900/30 text-gray-300 rounded">
                        {step}
                      </span>
                      {idx < platformSteps.length - 1 && (
                        <span className="text-cyan-500 font-bold">→</span>
                      )}
                    </motion.span>
                  ))}
                </div>
              </motion.div>

              {/* CEO Info */}
              <motion.div
                className="flex items-center gap-4"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <motion.div
                  className="w-12 h-12 flex items-center justify-center rounded-full border border-cyan-500/40"
                  whileHover={{ scale: 1.1, borderColor: "rgba(0, 229, 255, 0.6)" }}
                >
                  <img
                    src="/cityai-logo-icon.png"
                    alt="CITY-AI"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                </motion.div>
                <div>
                  <div className="text-white text-sm font-semibold">정종모 대표이사</div>
                  <div className="text-gray-500 text-xs">CEO, CITY-AI 주식회사</div>
                </div>
              </motion.div>
            </div>

            {/* Visual Section */}
            <div className="relative">
              <motion.div
                className="w-full h-80 rounded-xl overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://readdy.ai/api/search-image?query=AI%20surveillance%20control%20room%20with%20multiple%20screens%20showing%20CCTV%20feeds%20digital%20twin%203D%20city%20model%20real-time%20data%20analytics%20interface%20dark%20modern%20tech%20environment%20with%20cyan%20blue%20glowing%20displays%20operators%20working%20monitoring%20systems%20futuristic%20command%20center&width=700&height=500&seq=about001&orientation=landscape"
                  alt="CITY-AI ADX 플랫폼"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 to-transparent rounded-xl" />
              </motion.div>

              {/* Floating Cards */}
              <FloatingElement
                className="absolute -bottom-4 -left-4 bg-brand-darkCard border border-cyan-900/50 rounded-lg p-4 backdrop-blur-sm shadow-card"
                amplitude={5}
                duration={6}
              >
                <div className="text-cyan-400 text-lg font-bold">ADX Platform</div>
                <div className="text-gray-400 text-xs">AI + Digital Twin 통합</div>
              </FloatingElement>

              <FloatingElement
                className="absolute -top-4 -right-4 bg-brand-darkCard border border-cyan-900/50 rounded-lg p-4 backdrop-blur-sm shadow-card"
                amplitude={5}
                duration={7}
              >
                <div className="flex items-center gap-2">
                  <motion.div
                    className="w-2 h-2 bg-cyan-400 rounded-full"
                    animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="text-cyan-400 text-xs">컨설팅 → 솔루션 구축</span>
                </div>
              </FloatingElement>
            </div>
          </div>
        </AnimatedSection>

        {/* Highlights Grid */}
        <StaggerContainer
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-16"
          staggerDelay={0.1}
        >
          {highlights.map((item) => (
            <StaggerItem key={item.title}>
              <Card3D>
                <motion.div
                  className="bg-brand-darkCard border border-cyan-900/30 rounded-lg p-5 h-full"
                  whileHover={{ borderColor: "rgba(0, 229, 255, 0.4)" }}
                >
                  <motion.div
                    className="w-10 h-10 flex items-center justify-center mb-3"
                    whileHover={{ rotate: 5, scale: 1.1 }}
                  >
                    <i className={`${item.icon} text-cyan-400 text-xl`} />
                  </motion.div>
                  <div className="text-white text-sm font-semibold mb-1">{item.title}</div>
                  <div className="text-gray-500 text-xs leading-relaxed">{item.desc}</div>
                </motion.div>
              </Card3D>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}
